import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PawPrint,
  Plus,
  Edit2,
  Trash2,
  X,
  Upload,
  Heart,
  Activity,
  Weight,
  Calendar,
  AlertCircle,
  Camera,
  Check,
} from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import {
  Pet,
  PetFormData,
  PetSpecies,
  ActivityLevel,
  BodyCondition,
  createEmptyPet,
  validatePetData,
  calculateDER,
  kgToLbs,
  lbsToKg,
  getBreedSuggestions,
} from '@/types/pet';
import { processImage, formatFileSize, isValidImageFile } from '@/utils/imageProcessor';

export const Pets: React.FC = () => {
  const { isLoading } = useAuth();
  const [pets, setPets] = useState<Pet[]>([]); // TODO: Load from API
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPet, setEditingPet] = useState<Pet | null>(null);
  const [formData, setFormData] = useState<PetFormData>(createEmptyPet());
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [showWeightInLbs, setShowWeightInLbs] = useState(false);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading your pets...</p>
        </div>
      </div>
    );
  }

  const handleAddPet = () => {
    setEditingPet(null);
    setFormData(createEmptyPet());
    setFormErrors([]);
    setPhotoPreview(null);
    setIsModalOpen(true);
  };

  const handleEditPet = (pet: Pet) => {
    setEditingPet(pet);
    setFormData({
      name: pet.name,
      species: pet.species,
      breed: pet.breed,
      age: pet.age,
      weight: pet.weight,
      activityLevel: pet.activityLevel,
      bodyCondition: pet.bodyCondition,
      allergies: pet.allergies,
      specialNeeds: pet.specialNeeds,
    });
    setPhotoPreview(pet.photoUrl || null);
    setFormErrors([]);
    setIsModalOpen(true);
  };

  const handleDeletePet = async (petId: string) => {
    if (!confirm('Are you sure you want to delete this pet? This action cannot be undone.')) {
      return;
    }

    try {
      // TODO: Implement API call
      console.log('Deleting pet:', petId);
      setPets((prev) => prev.filter((p) => p.id !== petId));
      alert('Pet deleted successfully! (Backend endpoint not yet implemented)');
    } catch (error) {
      console.error('Failed to delete pet:', error);
      alert('Failed to delete pet. Please try again.');
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isValidImageFile(file)) {
      alert('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    setIsProcessingImage(true);

    try {
      // Process image (resize to 1200x1200, 85% quality)
      const processed = await processImage(file, {
        maxWidth: 1200,
        maxHeight: 1200,
        quality: 0.85,
        outputFormat: 'image/jpeg',
      });

      console.log(`Image processed: ${formatFileSize(processed.originalSize)} → ${formatFileSize(processed.processedSize)}`);

      setFormData((prev) => ({ ...prev, photo: processed.file }));
      setPhotoPreview(processed.dataUrl);
    } catch (error) {
      console.error('Failed to process image:', error);
      alert('Failed to process image. Please try again.');
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleSavePet = async () => {
    // Validate form
    const errors = validatePetData(formData);
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSaving(true);
    setFormErrors([]);

    try {
      // TODO: Upload photo to Azure Blob Storage
      // TODO: Save pet data to Shopify metafield via API

      console.log('Saving pet:', formData);

      if (editingPet) {
        // Update existing pet
        setPets((prev) =>
          prev.map((p) =>
            p.id === editingPet.id
              ? {
                  ...p,
                  ...formData,
                  photoUrl: photoPreview || p.photoUrl,
                  updatedAt: new Date().toISOString(),
                }
              : p
          )
        );
        alert('Pet updated successfully! (Backend endpoint not yet implemented)');
      } else {
        // Add new pet
        const newPet: Pet = {
          id: `pet_${Date.now()}`,
          ...formData,
          photoUrl: photoPreview || undefined,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setPets((prev) => [...prev, newPet]);
        alert('Pet added successfully! (Backend endpoint not yet implemented)');
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to save pet:', error);
      alert('Failed to save pet. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAllergyAdd = (allergy: string) => {
    if (allergy.trim() && !formData.allergies.includes(allergy.trim())) {
      setFormData((prev) => ({
        ...prev,
        allergies: [...prev.allergies, allergy.trim()],
      }));
    }
  };

  const handleAllergyRemove = (allergy: string) => {
    setFormData((prev) => ({
      ...prev,
      allergies: prev.allergies.filter((a) => a !== allergy),
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-neutral-900 mb-2">My Pets</h1>
          <p className="text-neutral-600">Manage your furry friends and their profiles</p>
        </div>
        <Button variant="primary" gradient onClick={handleAddPet}>
          <Plus className="w-5 h-5 mr-2" />
          Add Pet
        </Button>
      </div>

      {/* Summary Stats */}
      <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <PawPrint className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Total Pets</p>
                  <p className="text-2xl font-bold text-neutral-900">{pets.length}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-success-50 rounded-lg">
                  <Heart className="w-6 h-6 text-success-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Dogs</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {pets.filter((p) => p.species === 'dog').length}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card>
            <CardBody className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <Heart className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Cats</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {pets.filter((p) => p.species === 'cat').length}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </motion.div>

      {/* Pets List */}
      <div className="space-y-4">
        {pets.length === 0 ? (
          <Card>
            <CardBody className="p-12 text-center">
              <PawPrint className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Pets Yet</h3>
              <p className="text-neutral-600 mb-6">
                Add your first pet to start managing their profile and nutrition needs.
              </p>
              <Button variant="primary" gradient onClick={handleAddPet}>
                <Plus className="w-5 h-5 mr-2" />
                Add Your First Pet
              </Button>
            </CardBody>
          </Card>
        ) : (
          pets.map((pet) => {
            const calories = calculateDER(pet.weight, pet.activityLevel, pet.bodyCondition);

            return (
              <motion.div key={pet.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardBody className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Pet Photo */}
                      <div className="flex-shrink-0">
                        <div className="w-32 h-32 rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center">
                          {pet.photoUrl ? (
                            <img src={pet.photoUrl} alt={pet.name} className="w-full h-full object-cover" />
                          ) : (
                            <PawPrint className="w-16 h-16 text-neutral-300" />
                          )}
                        </div>
                      </div>

                      {/* Pet Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-2xl font-bold text-neutral-900 mb-1">{pet.name}</h3>
                            <p className="text-neutral-600">
                              {pet.breed} • {pet.species === 'dog' ? 'Dog' : 'Cat'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditPet(pet)}
                              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeletePet(pet.id)}
                              className="p-2 text-error-600 hover:bg-error-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-neutral-500 mb-1 flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Age
                            </p>
                            <p className="font-medium text-neutral-900">{pet.age} years</p>
                          </div>
                          <div>
                            <p className="text-sm text-neutral-500 mb-1 flex items-center gap-1">
                              <Weight className="w-4 h-4" />
                              Weight
                            </p>
                            <p className="font-medium text-neutral-900">
                              {pet.weight} kg ({kgToLbs(pet.weight)} lbs)
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-neutral-500 mb-1 flex items-center gap-1">
                              <Activity className="w-4 h-4" />
                              Activity
                            </p>
                            <p className="font-medium text-neutral-900 capitalize">{pet.activityLevel}</p>
                          </div>
                          <div>
                            <p className="text-sm text-neutral-500 mb-1">Body</p>
                            <p className="font-medium text-neutral-900 capitalize">{pet.bodyCondition}</p>
                          </div>
                        </div>

                        {/* Calorie Recommendations */}
                        <div className="bg-gradient-cool rounded-lg p-4">
                          <p className="text-sm font-medium text-neutral-900 mb-2">Daily Calorie Needs</p>
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="text-2xl font-bold text-primary-600">{calories.der} kcal/day</p>
                              <p className="text-xs text-neutral-600">RER: {calories.rer} kcal</p>
                            </div>
                            <div className="flex-1 text-sm text-neutral-700">{calories.recommendation}</div>
                          </div>
                        </div>

                        {/* Allergies */}
                        {pet.allergies.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-neutral-700 mb-2">Allergies:</p>
                            <div className="flex flex-wrap gap-2">
                              {pet.allergies.map((allergy) => (
                                <span
                                  key={allergy}
                                  className="px-3 py-1 bg-warning-50 text-warning-700 text-sm rounded-full"
                                >
                                  {allergy}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Special Needs */}
                        {pet.specialNeeds && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-neutral-700 mb-1">Special Needs:</p>
                            <p className="text-sm text-neutral-600">{pet.specialNeeds}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Add/Edit Pet Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-neutral-200 sticky top-0 bg-white z-10">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-neutral-900">
                    {editingPet ? 'Edit Pet' : 'Add New Pet'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 text-neutral-500 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Form Errors */}
                {formErrors.length > 0 && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-error-900 mb-1">Please fix the following errors:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {formErrors.map((error, index) => (
                            <li key={index} className="text-sm text-error-700">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Pet Photo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-32 rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center border-2 border-dashed border-neutral-300">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-12 h-12 text-neutral-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                        id="pet-photo-input"
                        disabled={isProcessingImage}
                      />
                      <label htmlFor="pet-photo-input">
                        <Button variant="outline" as="span" disabled={isProcessingImage}>
                          {isProcessingImage ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Photo
                            </>
                          )}
                        </Button>
                      </label>
                      <p className="text-xs text-neutral-500 mt-2">
                        JPG, PNG or WebP. Max 10MB. Will be resized to 1200x1200.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Pet Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Max, Bella"
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Species *</label>
                    <select
                      value={formData.species}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, species: e.target.value as PetSpecies, breed: '' }))
                      }
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="dog">Dog</option>
                      <option value="cat">Cat</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Breed *</label>
                    <select
                      value={formData.breed}
                      onChange={(e) => setFormData((prev) => ({ ...prev, breed: e.target.value }))}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select breed</option>
                      {getBreedSuggestions(formData.species).map((breed) => (
                        <option key={breed} value={breed}>
                          {breed}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Age (years) *</label>
                    <input
                      type="number"
                      value={formData.age || ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, age: parseFloat(e.target.value) || 0 }))}
                      min="0"
                      max="30"
                      step="0.5"
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-neutral-700">Weight *</label>
                      <button
                        type="button"
                        onClick={() => setShowWeightInLbs(!showWeightInLbs)}
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        {showWeightInLbs ? 'Show kg' : 'Show lbs'}
                      </button>
                    </div>
                    <input
                      type="number"
                      value={showWeightInLbs ? kgToLbs(formData.weight) || '' : formData.weight || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setFormData((prev) => ({
                          ...prev,
                          weight: showWeightInLbs ? lbsToKg(value) : value,
                        }));
                      }}
                      min="0"
                      max={showWeightInLbs ? '220' : '100'}
                      step="0.1"
                      placeholder={showWeightInLbs ? 'lbs' : 'kg'}
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Activity & Body Condition */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Activity Level *</label>
                    <select
                      value={formData.activityLevel}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, activityLevel: e.target.value as ActivityLevel }))
                      }
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="low">Low (Indoor, minimal exercise)</option>
                      <option value="moderate">Moderate (Regular walks/play)</option>
                      <option value="high">High (Very active, working dog)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Body Condition *</label>
                    <select
                      value={formData.bodyCondition}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, bodyCondition: e.target.value as BodyCondition }))
                      }
                      className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="underweight">Underweight (Ribs visible)</option>
                      <option value="ideal">Ideal (Healthy weight)</option>
                      <option value="overweight">Overweight (Ribs hard to feel)</option>
                    </select>
                  </div>
                </div>

                {/* Allergies */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Allergies</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Add allergy and press Enter"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAllergyAdd(e.currentTarget.value);
                          e.currentTarget.value = '';
                        }
                      }}
                      className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.allergies.map((allergy) => (
                      <span
                        key={allergy}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-warning-50 text-warning-700 text-sm rounded-full"
                      >
                        {allergy}
                        <button
                          type="button"
                          onClick={() => handleAllergyRemove(allergy)}
                          className="hover:text-warning-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Special Needs */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Special Needs</label>
                  <textarea
                    value={formData.specialNeeds}
                    onChange={(e) => setFormData((prev) => ({ ...prev, specialNeeds: e.target.value }))}
                    placeholder="Any special dietary requirements or medical conditions..."
                    rows={3}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Calorie Preview */}
                {formData.weight > 0 && (
                  <div className="bg-gradient-cool rounded-lg p-4">
                    <p className="text-sm font-medium text-neutral-900 mb-2">Daily Calorie Needs Preview</p>
                    {(() => {
                      const calories = calculateDER(formData.weight, formData.activityLevel, formData.bodyCondition);
                      return (
                        <>
                          <p className="text-2xl font-bold text-primary-600 mb-1">{calories.der} kcal/day</p>
                          <p className="text-sm text-neutral-700">{calories.recommendation}</p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-neutral-200 sticky bottom-0 bg-white">
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button variant="primary" gradient onClick={handleSavePet} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {editingPet ? 'Update Pet' : 'Add Pet'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Pets;
